import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileBarChart, Download, Eye, RefreshCw, AlertCircle, Loader, Search, Calendar, User, FileText, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { supabase, STORAGE_BUCKET } from '../lib/supabase';
import { userTierService } from '../lib/userTierService';

interface SummaryItem {
  id: string;
  name: string;
  path: string;
  size: number;
  type: string;
  created_at: string;
  metadata?: any;
  // Parsed metadata from filename
  submissionId?: string;
  filerName?: string;
  originalFileName?: string;
  // Filing type information
  filingType?: string;
  expressComment?: number;
  // Express comment specific
  isExpressComment?: boolean;
  // Summary content
  content?: string;
  overallSummary?: string;
}

interface FileItem {
  id: string;
  name: string;
  path: string;
  size: number;
  type: string;
  created_at: string;
  metadata?: any;
  // Parsed metadata from filename
  submissionId?: string;
  filerName?: string;
  originalFileName?: string;
  // Filing type information
  filingType?: string;
  expressComment?: number;
  // Express comment specific
  isExpressComment?: boolean;
}

interface SummaryGenerationStatus {
  isGenerating: boolean;
  isReady: boolean;
  summary?: string;
  error?: string;
}

export const SummariesPage: React.FC = () => {
  const { docketNumber } = useParams<{ docketNumber: string }>();
  const navigate = useNavigate();
  
  // Summary management state
  const [summaries, setSummaries] = useState<SummaryItem[]>([]);
  const [filteredSummaries, setFilteredSummaries] = useState<SummaryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSummary, setSelectedSummary] = useState<SummaryItem | null>(null);
  
  // File management state
  const [files, setFiles] = useState<FileItem[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<FileItem[]>([]);
  const [individualSummaryStatus, setIndividualSummaryStatus] = useState<{ [key: string]: SummaryGenerationStatus }>({});
  
  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'size'>('date');
  const [activeTab, setActiveTab] = useState<'summaries' | 'files'>('summaries');
  
  // Expansion state for summary content
  const [expandedSummaries, setExpandedSummaries] = useState<{ [key: string]: boolean }>({});

  // Load summaries and files from Supabase when component mounts
  useEffect(() => {
    if (docketNumber) {
      loadSummaries();
      loadFiles();
    }
  }, [docketNumber]);

  // Debug function - expose to window for testing
  useEffect(() => {
    (window as any).testSummaryGeneration = () => {
      console.log('🧪 Testing summary generation...');
      console.log('Files available:', files.length);
      console.log('Docket number:', docketNumber);
      if (files.length > 0) {
        console.log('First file:', files[0]);
        handleCreateIndividualSummary(files[0].name);
      } else {
        console.log('No files available for testing');
      }
    };
  }, [files, docketNumber]);

  // Filter and search summaries
  useEffect(() => {
    let filtered = [...summaries];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(summary => 
        (summary.originalFileName || summary.name).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (summary.filerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (summary.content || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.originalFileName || a.name).localeCompare(b.originalFileName || b.name);
        case 'size':
          return b.size - a.size;
        case 'date':
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

    setFilteredSummaries(filtered);
  }, [summaries, searchTerm, sortBy]);

  // Filter and search files
  useEffect(() => {
    let filtered = [...files];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(file => 
        (file.originalFileName || file.name).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (file.filerName || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.originalFileName || a.name).localeCompare(b.originalFileName || b.name);
        case 'size':
          return b.size - a.size;
        case 'date':
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

    setFilteredFiles(filtered);
  }, [files, searchTerm, sortBy]);

  const loadSummaries = async () => {
    if (!docketNumber) return;

    try {
      setLoading(true);
      setError(null);

      // Load existing summaries from storage
      await loadExistingSummaries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load summaries');
    } finally {
      setLoading(false);
    }
  };

  const loadFiles = async () => {
    if (!docketNumber) return;

    try {
      // Load files from all filing type subdirectories
      let allStorageFiles: any[] = [];

      // Get all subdirectories in the docket folder
      const { data: subdirectories, error: listError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .list(`ecfs/${docketNumber}`, {
          limit: 1000,
          offset: 0,
        });

      if (listError) {
        console.warn('Failed to list docket subdirectories:', listError);
        return;
      }

      // Filter for subdirectories only (folders have null id)
      const filingTypeFolders = (subdirectories || []).filter(item => item.id === null);

      // Load files from each filing type subdirectory
      for (const folder of filingTypeFolders) {
        const subdirectoryPath = `ecfs/${docketNumber}/${folder.name}`;
        
        const { data: typeFiles, error: typeListError } = await supabase.storage
          .from(STORAGE_BUCKET)
          .list(subdirectoryPath, {
            limit: 1000,
            offset: 0,
          });

        if (!typeListError && typeFiles) {
          // Add filing type information to files and set the correct path
          const filesWithType = typeFiles.map((file: any) => ({
            ...file,
            filingType: folder.name,
            path: `${subdirectoryPath}/${file.name}` // Set the full path including filing type subdirectory
          }));
          allStorageFiles.push(...filesWithType);
        }
      }

      const fileItems: FileItem[] = (allStorageFiles || [])
        .filter(file => file.id !== null)
        .map(file => {
          const parts = file.name.split('___');
          let submissionId: string | undefined;
          let filerName: string | undefined;
          let originalFileName: string | undefined;

          if (parts.length >= 3) {
            submissionId = parts[0] !== 'UNKNOWN' ? parts[0] : undefined;
            filerName = parts[1];
            originalFileName = parts.slice(2).join('___');
          } else {
            originalFileName = file.name;
          }

          // Check if this is an express comment
          const pathParts = file.path.split('/');
          const isExpressComment = pathParts.includes('EXPRESS-COMMENT') || 
                                  file.metadata?.contentType === 'express_comment' ||
                                  file.metadata?.expressComment === 1;

          return {
            id: file.id || file.name,
            name: file.name,
            path: file.path,
            size: file.metadata?.size || 0,
            type: file.metadata?.mimetype || (isExpressComment ? 'text/plain' : 'application/octet-stream'),
            created_at: file.created_at || file.updated_at || new Date().toISOString(),
            metadata: file.metadata,
            submissionId,
            filerName,
            originalFileName,
            filingType: file.filingType,
            expressComment: file.metadata?.expressComment || undefined,
            isExpressComment,
          };
        })
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setFiles(fileItems);
      console.log('📁 Files loaded:', fileItems.length, 'files from filing type subdirectories');
      console.log('📁 Files details:', fileItems.map(f => ({
        name: f.name,
        filingType: f.filingType,
        path: f.path,
        isExpressComment: f.isExpressComment
      })));
    } catch (err) {
      console.error('Error loading files:', err);
    }
  };

  const loadExistingSummaries = async () => {
    if (!docketNumber) return;

    try {
      // Look for summaries in the correct location: summaries/{docketNumber}/individual_summaries/
      const summaryFolder = `summaries/${docketNumber}/individual_summaries`;
      
      // First, try to list the main individual_summaries folder
      const { data: summaryFiles, error: summaryError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .list(summaryFolder, {
          limit: 1000,
          offset: 0,
        });

      const allSummaries: SummaryItem[] = [];

      if (!summaryError && summaryFiles) {
        
        // Load summaries from the main folder
        for (const file of summaryFiles) {
          if (file.id !== null && (file.name.endsWith('.txt') || file.name.endsWith('.summary'))) {
            try {
              const filePath = `${summaryFolder}/${file.name}`;
              const { data: fileData, error: downloadError } = await supabase.storage
                .from(STORAGE_BUCKET)
                .download(filePath);

              if (downloadError) {
                console.warn(`Failed to download ${file.name}:`, downloadError);
                continue;
              }

              const content = await fileData.text();
              const summaryItem = createSummaryItem(file, filePath, content);
              allSummaries.push(summaryItem);
            } catch (err) {
              console.warn(`Error processing ${file.name}:`, err);
            }
          }
        }
      }

      // Also check for filing type subdirectories
      if (!summaryError && summaryFiles) {
        const filingTypeFolders = summaryFiles.filter(item => item.id === null);
        
        for (const folder of filingTypeFolders) {
          const subdirectoryPath = `${summaryFolder}/${folder.name}`;
          await loadSummariesRecursively(subdirectoryPath, allSummaries);
        }
      }

      setSummaries(allSummaries);
    } catch (err) {
      console.error('Error loading summaries:', err);
      throw err;
    }
  };

  const loadSummariesRecursively = async (folderPath: string, allSummaries: SummaryItem[]): Promise<void> => {
    try {
      const { data: storageItems, error: listError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .list(folderPath, {
          limit: 1000,
          offset: 0,
        });

      if (listError) {
        console.warn(`Failed to list ${folderPath}:`, listError);
        return;
      }

      if (!storageItems) return;

      for (const item of storageItems) {
        const itemPath = `${folderPath}/${item.name}`;
        
        if (item.id === null) {
          // This is a folder, recursively search it
          await loadSummariesRecursively(itemPath, allSummaries);
        } else {
          // This is a file, check if it's a summary file (.txt or .summary)
          if (item.name.endsWith('.txt') || item.name.endsWith('.summary')) {
            try {
              const { data: fileData, error: downloadError } = await supabase.storage
                .from(STORAGE_BUCKET)
                .download(itemPath);

              if (downloadError) {
                console.warn(`Failed to download ${item.name}:`, downloadError);
                continue;
              }

              const content = await fileData.text();
              const summaryItem = createSummaryItem(item, folderPath, content);
              allSummaries.push(summaryItem);
            } catch (err) {
              console.warn(`Error processing ${item.name}:`, err);
            }
          }
        }
      }
    } catch (err) {
      console.warn(`Error loading summaries from ${folderPath}:`, err);
    }
  };

  const createSummaryItem = (file: any, filePath: string, content: string): SummaryItem => {
    // Parse filename to extract metadata
    const parts = file.name.split('___');
    let submissionId: string | undefined;
    let filerName: string | undefined;
    let originalFileName: string | undefined;

    if (parts.length >= 3) {
      submissionId = parts[0] !== 'UNKNOWN' ? parts[0] : undefined;
      filerName = parts[1];
      originalFileName = parts.slice(2).join('___').replace(/\.(txt|summary)$/, '');
    } else {
      originalFileName = file.name.replace(/\.(txt|summary)$/, '');
    }

    // Filing type information is not available for summaries at this time
    // Summaries are stored directly in individual_summaries folder without type subfolders
    const filingType: string | undefined = undefined;
    
    // Check if this is an express comment
    const pathParts = filePath.split('/');
    
    // Debug logging for Express comment detection
    console.log('🔍 Summary file analysis:', {
      fileName: file.name,
      filePath,
      pathParts,
      metadata: file.metadata,
      hasMetadata: !!file.metadata,
      metadataKeys: file.metadata ? Object.keys(file.metadata) : []
    });
    
    const isExpressComment = pathParts.includes('EXPRESS-COMMENT') || 
                            file.metadata?.contentType === 'express_comment' ||
                            file.metadata?.expressComment === 1;
    
    console.log('⚡ Summary Express comment check:', {
      fileName: file.name,
      pathIncludesExpress: pathParts.includes('EXPRESS-COMMENT'),
      contentTypeExpress: file.metadata?.contentType === 'express_comment',
      expressCommentFlag: file.metadata?.expressComment === 1,
      isExpressComment
    });

    // Extract overall_summary from content if it exists
    let overallSummary: string | undefined;
    try {
      // Try to parse as JSON first
      const jsonMatch = content.match(/"overall_summary":\s*"([^"]+)"/);
      if (jsonMatch) {
        overallSummary = jsonMatch[1];
      } else {
        // Fallback: look for overall_summary in various formats
        const overallMatch = content.match(/overall_summary["\s]*:?\s*["\s]*([^"}\n]+)/i);
        if (overallMatch) {
          overallSummary = overallMatch[1].trim();
        }
      }
    } catch (err) {
      console.warn('Error parsing overall_summary:', err);
    }

    return {
      id: file.id || file.name,
      name: file.name,
      path: filePath,
      size: file.metadata?.size || 0,
      type: file.metadata?.mimetype || 'text/plain',
      created_at: file.created_at || file.updated_at || new Date().toISOString(),
      metadata: file.metadata,
      submissionId,
      filerName,
      originalFileName,
      filingType,
      expressComment: file.metadata?.expressComment || undefined,
      isExpressComment,
      content,
      overallSummary,
    };
  };

  const handleSummaryDownload = async (summary: SummaryItem) => {
    try {
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .download(summary.path);

      if (error) {
        throw error;
      }

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${summary.originalFileName || summary.name}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download summary. Please try again.');
    }
  };

  const toggleSummaryExpansion = (summaryId: string) => {
    setExpandedSummaries(prev => ({
      ...prev,
      [summaryId]: !prev[summaryId]
    }));
  };

  // Individual summary generation function
  const handleCreateIndividualSummary = async (filename: string) => {
    if (!docketNumber) {
      console.error('No docket number available');
      return;
    }

    console.log('🚀 Starting individual summary generation for:', filename);

    // Check if user can perform summary action
    console.log('🔍 Checking user tier permissions...');
    const canGenerateSummary = await userTierService.canPerformAction('summary');
    console.log('🔍 User can generate summary:', canGenerateSummary);
    
    if (!canGenerateSummary) {
      const errorMsg = 'You have reached your summary generation limit for this month. Please upgrade your plan to continue generating summaries.';
      console.error('❌ User tier limit reached:', errorMsg);
      setError(errorMsg);
      return;
    }

    try {
      setIndividualSummaryStatus(prev => ({
        ...prev,
        [filename]: { isGenerating: true, isReady: false }
      }));

      // Get file information including the full path
      const file = files.find(f => f.name === filename);
      if (!file) {
        throw new Error(`File ${filename} not found in files list`);
      }

      console.log('📁 File found:', {
        name: file.name,
        path: file.path,
        filingType: file.filingType,
        size: file.size
      });

      // Get filing type for this file from the file object (set during file loading)
      const filingType = file?.filingType;
      
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/prepare-individual-summary`;
      
      const requestBody = { 
        docketNumber,
        filename,
        filePath: file.path, // Pass the full file path including subdirectory
        filing_type: filingType
      };

      console.log('📤 Making API request to:', apiUrl);
      console.log('📤 Request body:', requestBody);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('📥 API response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ API error response:', errorData);
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const summaryText = await response.text();
      console.log('✅ Summary generated successfully:', {
        length: summaryText.length,
        preview: summaryText.substring(0, 200) + '...'
      });
      
      setIndividualSummaryStatus(prev => ({
        ...prev,
        [filename]: { 
          isGenerating: false, 
          isReady: true, 
          summary: summaryText 
        }
      }));

      // Refresh summaries to show the newly generated one
      console.log('🔄 Refreshing summaries list...');
      await loadSummaries();

    } catch (err) {
      console.error('❌ Individual summary generation failed:', err);
      setIndividualSummaryStatus(prev => ({
        ...prev,
        [filename]: { 
          isGenerating: false, 
          isReady: false, 
          error: err instanceof Error ? err.message : 'Summary generation failed' 
        }
      }));
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get count of files per filer
  const getFilerFileCount = (filerName: string) => {
    const count = files.filter(file => file.filerName === filerName).length;
    console.log(`🔢 SummariesPage: Filer "${filerName}" has ${count} files`);
    return count;
  };

  // Check if a file should have summary generation
  const shouldGenerateSummaryForFile = (file: FileItem) => {
    // Express Comments should always allow summary generation
    if (file.isExpressComment) {
      return true;
    }
    
    // For regular files, check if filename contains "comment"
    const filenameLower = file.name.toLowerCase();
    return filenameLower.includes('comment') || filenameLower.includes('comments');
  };

  const truncateText = (text: string, maxLength: number = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header with Breadcrumb */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate('/library')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to My Dockets</span>
          </button>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Summaries - Docket {docketNumber}
            </h1>
            <p className="text-gray-600 mt-2">
              {activeTab === 'summaries' 
                ? `${summaries.length} summaries • ${filteredSummaries.length} shown`
                : `${files.length} files • ${filteredFiles.length} shown`
              }
            </p>
          </div>
          
          <button
            onClick={() => {
              if (activeTab === 'summaries') {
                loadSummaries();
              } else {
                loadFiles();
              }
            }}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200 disabled:opacity-50"
          >
            {loading ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('summaries')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'summaries'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FileBarChart className="w-4 h-4" />
                <span>Summaries ({summaries.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('files')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'files'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4" />
                <span>Files ({files.length})</span>
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search summaries, filers, or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Sort */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'name' | 'size')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
            >
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="size">Sort by Size</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading summaries...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-8 text-center">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md mx-auto">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
              <h3 className="text-red-800 font-semibold mb-2">Error Loading Summaries</h3>
              <p className="text-red-600 text-sm mb-4">{error}</p>
              <button
                onClick={loadSummaries}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && activeTab === 'summaries' && filteredSummaries.length === 0 && (
          <div className="p-8 text-center">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 max-w-md mx-auto">
              <FileBarChart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-gray-900 font-semibold mb-2">
                {searchTerm ? 'No Summaries Match Your Search' : 'No Summaries Found'}
              </h3>
              <p className="text-gray-600 text-sm">
                {searchTerm 
                  ? 'Try adjusting your search terms.'
                  : 'No summaries have been generated for this docket yet.'
                }
              </p>
            </div>
          </div>
        )}

        {!loading && !error && activeTab === 'files' && filteredFiles.length === 0 && (
          <div className="p-8 text-center">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 max-w-md mx-auto">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-gray-900 font-semibold mb-2">
                {searchTerm ? 'No Files Match Your Search' : 'No Files Found'}
              </h3>
              <p className="text-gray-600 text-sm">
                {searchTerm 
                  ? 'Try adjusting your search terms.'
                  : 'No files have been uploaded for this docket yet.'
                }
              </p>
            </div>
          </div>
        )}

        {/* Summaries List */}
        {!loading && !error && activeTab === 'summaries' && filteredSummaries.length > 0 && (
          <div className="space-y-4">
            {filteredSummaries.map((summary) => (
              <div
                key={summary.id}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-all duration-200 hover:border-green-300"
              >
                {/* Header Section with Icon, Title, Metadata, and Action Buttons */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-4 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <FileBarChart className="w-5 h-5 text-green-600" />
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 leading-tight" title={summary.originalFileName || summary.name}>
                        <span className="line-clamp-2">
                          {summary.originalFileName || summary.name}
                        </span>
                      </h3>
                      
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                          <div className="flex items-center space-x-2 min-w-0">
                            <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div className="flex items-center space-x-2 min-w-0">
                              <span className="text-gray-600 truncate" title={summary.filerName || 'Unknown Filer'}>
                                {summary.filerName || 'Unknown Filer'}
                              </span>
                              {/* Document Count Tag - only show if more than 1 file */}
                              {summary.filerName && (() => {
                                const fileCount = getFilerFileCount(summary.filerName);
                                console.log(`🏷️ SummariesPage: Rendering count tag for "${summary.filerName}": ${fileCount} files`);
                                return fileCount > 1 ? (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full border border-blue-200 flex-shrink-0">
                                    {fileCount} docs
                                  </span>
                                ) : null;
                              })()}
                            </div>
                          </div>
                        
                        <div className="flex items-center space-x-2 min-w-0">
                          <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-600">{formatFileSize(summary.size)}</span>
                        </div>
                        
                        <div className="flex items-center space-x-2 min-w-0">
                          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-600">{formatDate(summary.created_at)}</span>
                        </div>
                        
                        <div className="flex items-center space-x-2 min-w-0">
                          {summary.filingType && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                              {summary.filingType}
                            </span>
                          )}
                          {summary.isExpressComment && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
                              Express
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {summary.submissionId && (
                        <p className="text-gray-500 text-xs mt-2">
                          Submission ID: {summary.submissionId}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => setSelectedSummary(summary)}
                      className="flex items-center space-x-1 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 hover:text-blue-800 rounded-lg transition-colors duration-200 text-sm"
                    >
                      <Eye className="w-4 h-4" />
                      <span>Preview</span>
                    </button>
                    
                    <button
                      onClick={() => handleSummaryDownload(summary)}
                      className="flex items-center space-x-1 px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 hover:text-green-800 rounded-lg transition-colors duration-200 text-sm"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download</span>
                    </button>
                  </div>
                </div>

                {/* Summary Content - Full Width */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <h4 className="text-sm font-semibold text-green-800">Summary</h4>
                    </div>
                    <button
                      onClick={() => toggleSummaryExpansion(summary.id)}
                      className="flex items-center space-x-1 text-green-600 hover:text-green-800 text-sm font-medium transition-colors duration-200"
                    >
                      <span>{expandedSummaries[summary.id] ? 'Show Less' : 'Show More'}</span>
                      {expandedSummaries[summary.id] ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  
                  <div className="text-sm text-gray-800 leading-relaxed">
                    {expandedSummaries[summary.id] ? (
                      <div className="prose prose-sm max-w-none">
                        <div className="whitespace-pre-wrap font-normal">
                          {summary.overallSummary || summary.content}
                        </div>
                      </div>
                    ) : (
                      <div className="relative">
                        <p className="font-normal">
                          {summary.overallSummary ? (
                            truncateText(summary.overallSummary)
                          ) : (
                            truncateText(summary.content || '')
                          )}
                        </p>
                        {((summary.overallSummary || summary.content || '').length > 200) && (
                          <div className="absolute bottom-0 right-0 bg-gradient-to-l from-green-50 to-transparent pl-8">
                            <span className="text-green-600 text-xs">...</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Files List */}
        {!loading && !error && activeTab === 'files' && filteredFiles.length > 0 && (
          <div className="space-y-4">
            {filteredFiles.map((file) => {
              const hasSummary = summaries.some(summary => 
                summary.originalFileName === file.originalFileName || 
                summary.name === file.name
              );
              const summaryStatus = individualSummaryStatus[file.name];
              
              return (
                <div
                  key={file.id}
                  className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-all duration-200 hover:border-blue-300"
                >
                  {/* Header Section with Icon, Title, Metadata, and Action Buttons */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-4 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 leading-tight" title={file.originalFileName || file.name}>
                          <span className="line-clamp-2">
                            {file.originalFileName || file.name}
                          </span>
                        </h3>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                          <div className="flex items-center space-x-2 min-w-0">
                            <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div className="flex items-center space-x-2 min-w-0">
                              <span className="text-gray-600 truncate" title={file.filerName || 'Unknown Filer'}>
                                {file.filerName || 'Unknown Filer'}
                              </span>
                              {/* Document Count Tag - only show if more than 1 file */}
                              {file.filerName && (() => {
                                const fileCount = getFilerFileCount(file.filerName);
                                console.log(`🏷️ SummariesPage Files: Rendering count tag for "${file.filerName}": ${fileCount} files`);
                                return fileCount > 1 ? (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full border border-blue-200 flex-shrink-0">
                                    {fileCount} docs
                                  </span>
                                ) : null;
                              })()}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2 min-w-0">
                            <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600">{formatFileSize(file.size)}</span>
                          </div>
                          
                          <div className="flex items-center space-x-2 min-w-0">
                            <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600">{formatDate(file.created_at)}</span>
                          </div>
                          
                          <div className="flex items-center space-x-2 min-w-0">
                            {file.filingType && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                {file.filingType}
                              </span>
                            )}
                            {file.isExpressComment && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                                Express
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {file.submissionId && (
                          <p className="text-gray-500 text-xs mt-2">
                            Submission ID: {file.submissionId}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2 ml-4">
                      {hasSummary ? (
                        <div className="flex items-center space-x-1 px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm">
                          <FileBarChart className="w-4 h-4" />
                          <span>Summary Available</span>
                        </div>
                      ) : summaryStatus?.isGenerating ? (
                        <div className="flex items-center space-x-1 px-3 py-2 bg-orange-50 text-orange-700 rounded-lg text-sm">
                          <Loader className="w-4 h-4 animate-spin" />
                          <span>Generating...</span>
                        </div>
                      ) : summaryStatus?.error ? (
                        <div className="flex items-center space-x-1 px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm">
                          <AlertCircle className="w-4 h-4" />
                          <span>Error</span>
                        </div>
                      ) : shouldGenerateSummaryForFile(file) ? (
                        <button
                          onClick={() => handleCreateIndividualSummary(file.name)}
                          className="flex items-center space-x-1 px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md text-sm"
                        >
                          <Sparkles className="w-4 h-4" />
                          <span>Generate Summary</span>
                        </button>
                      ) : (
                        <div className="flex items-center space-x-1 px-3 py-2 bg-gray-100 text-gray-500 rounded-lg text-sm">
                          <AlertCircle className="w-4 h-4" />
                          <span>No comment in filename</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Summary Status Display */}
                  {summaryStatus?.isReady && summaryStatus.summary && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mt-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <h4 className="text-sm font-semibold text-green-800">Generated Summary</h4>
                      </div>
                      <div className="text-sm text-gray-800 leading-relaxed">
                        <p className="font-normal">
                          {summaryStatus.summary.length > 200 
                            ? summaryStatus.summary.substring(0, 200) + '...'
                            : summaryStatus.summary
                          }
                        </p>
                      </div>
                    </div>
                  )}

                  {summaryStatus?.error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-red-500" />
                        <h4 className="text-sm font-semibold text-red-800">Summary Generation Failed</h4>
                      </div>
                      <p className="text-sm text-red-700">{summaryStatus.error}</p>
                      <button
                        onClick={() => handleCreateIndividualSummary(file.name)}
                        className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                      >
                        Try Again
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Summary Preview Modal */}
      {selectedSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="relative max-w-4xl w-full max-h-screen bg-white border border-gray-300 rounded-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-300">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 truncate" title={selectedSummary.originalFileName || selectedSummary.name}>
                  {selectedSummary.originalFileName || selectedSummary.name}
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  {formatFileSize(selectedSummary.size)} • {formatDate(selectedSummary.created_at)}
                  {selectedSummary.filerName && ` • ${selectedSummary.filerName}`}
                </p>
              </div>
              
              <button
                onClick={() => setSelectedSummary(null)}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 max-h-96 overflow-y-auto">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <h3 className="text-lg font-semibold text-green-800">Summary Content</h3>
                </div>
                <div className="prose prose-sm max-w-none">
                  <div className="whitespace-pre-wrap text-gray-800 leading-relaxed font-normal">
                    {selectedSummary.overallSummary || selectedSummary.content}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end p-6 border-t border-gray-300">
              <button
                onClick={() => handleSummaryDownload(selectedSummary)}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200"
              >
                <Download className="w-4 h-4" />
                <span>Download Summary</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
